import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { NotificationCard } from './notifications';
import { fetchUnreadCount, NOTIFICATIONS_REFRESH_EVENT, requestNotificationRefresh } from './notification_events';

const iconPaths = {
  home: <><path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10.5V20h13v-9.5M9.5 20v-6h5v6"/></>,
  location: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
  request: <><path d="M12 3v18M3 12h18"/><circle cx="12" cy="12" r="9"/></>,
  schedule: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4M17 3v4M3 10h18M8 14h3M13 14h3M8 17h3"/></>,
  invoice: <><path d="M6 3h9l4 4v14H6Z"/><path d="M15 3v5h4M9 12h7M9 16h7"/></>,
  notification: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></>,
  profile: <><circle cx="12" cy="8" r="4"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.4-2.4 1a7 7 0 0 0-1.8-1L14.4 3h-4.8l-.4 3.1a7 7 0 0 0-1.8 1L5 6.1 3 9.5 5.1 11a7 7 0 0 0 0 2L3 14.5l2 3.4 2.4-1a7 7 0 0 0 1.8 1l.4 3.1h4.8l.4-3.1a7 7 0 0 0 1.8-1l2.4 1 2-3.4-2.1-1.5a7 7 0 0 0 .1-1Z"/></>,
  logout: <><path d="M10 4H5v16h5M14 8l4 4-4 4M8 12h10"/></>
};
const Icon = ({ name }) => <span className="sidebarNavIcon" aria-hidden="true"><svg viewBox="0 0 24 24">{iconPaths[name]}</svg></span>;
const Badge = ({ count, className }) => count > 0 ? <span className={className}>{count > 99 ? '99+' : count}</span> : null;

export const Nav = ({ currentUser, collapsed, onToggleCollapsed, mobileOpen, onMobileClose, onMobileOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const popoverRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentStatus, setRecentStatus] = useState('idle');
  const [recentError, setRecentError] = useState('');

  const refreshCount = useCallback(() => {
    if (!currentUser?.userId) return;
    fetchUnreadCount().then(setUnreadCount).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    refreshCount();
    const interval = window.setInterval(refreshCount, 60000);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, refreshCount);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, refreshCount);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!popoverOpen) return undefined;
    setRecentStatus('loading');
    fetch('/apinotifications?limit=5')
      .then(response => {
        if (!response.ok) throw new Error('Unable to load recent notifications');
        return response.json();
      })
      .then(data => { setRecent(Array.isArray(data) ? data : []); setRecentStatus('ready'); })
      .catch(error => { setRecentError(error.message); setRecentStatus('error'); });
    const dismiss = event => {
      if (event.key === 'Escape' || (event.type === 'mousedown' && !popoverRef.current?.contains(event.target))) setPopoverOpen(false);
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [popoverOpen]);

  if (!currentUser?.userId) return null;

  const logout = async () => {
    const response = await fetch('/apilogout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (response.ok && await response.json()) {
      dispatch({ type: 'LOG_OUT', payload: null });
      sessionStorage.removeItem('currentUser');
      navigate('/login', { replace: true });
    }
  };

  const markAllRead = async () => {
    const response = await fetch('/apinotifications/read-all', { method: 'POST' });
    if (!response.ok) return setRecentError('Unable to mark notifications read');
    setRecent(items => items.map(item => ({ ...item, read_at: item.read_at || true })));
    setUnreadCount(0);
    requestNotificationRefresh();
  };

  const navItems = [
    ['/', 'Home', 'home'],
    ['/locations', 'Locations', 'location'],
    ['/serviceTickets', 'Service Requests', 'request'],
    ...(currentUser.isAdmin || currentUser.portalApp !== 'fire_pest' ? [['/Schedules', 'Schedules', 'schedule']] : []),
    ['/invoices', 'Invoices', 'invoice'],
    ['/notifications', 'Notifications', 'notification']
  ];

  return (
    <>
      <button type="button" className="mobileNavToggle" aria-label="Open navigation" aria-expanded={mobileOpen} onClick={onMobileOpen}>☰</button>
      <div className={`mobileNavOverlay ${mobileOpen ? 'open' : ''}`} onClick={onMobileClose} aria-hidden="true" />
      <aside className={`navWrapper ${collapsed ? 'sidebarCollapsed' : ''} ${mobileOpen ? 'mobileOpen' : ''}`} aria-label="Primary navigation">
        <div className="sidebarBrand">
          <NavLink to="/" className="sidebarLogoLink" onClick={onMobileClose}>
            <img className="fusaLogo" src="https://res.cloudinary.com/dof3z7iuh/image/upload/v1787773165/facilities-logo-1024x246_jkk6eb.webp" alt="Facilities USA" />
            <span className="fusaLogoCompact" aria-hidden="true">FUSA</span>
          </NavLink>
          <button type="button" className="sidebarMobileClose" aria-label="Close navigation" onClick={onMobileClose}>×</button>
        </div>
        <button type="button" className="sidebarCollapseButton" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={onToggleCollapsed}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} /></svg>
        </button>
        <nav className="sidebarNav">
          <span className="sidebarGroupLabel">Workspace</span>
          {navItems.map(([to, label, glyph]) => (
            <NavLink key={to} to={to} end={to === '/'} className="sidebarNavLink" onClick={onMobileClose} title={collapsed ? label : undefined}>
              <Icon name={glyph} /><span className="sidebarLinkText">{label}</span>
              {to === '/notifications' && <Badge count={unreadCount} className="sidebarNotificationBadge" />}
            </NavLink>
          ))}
        </nav>
        <div className="sidebarAccount">
          <span className="sidebarGroupLabel">Account</span>
          <NavLink to="/profile" className="sidebarNavLink" onClick={onMobileClose} title={collapsed ? 'Profile' : undefined}>
            <Icon name="profile" /><span className="sidebarLinkText">Profile</span>
          </NavLink>
          {currentUser.isAdmin && <NavLink to="/settings" className="sidebarNavLink" onClick={onMobileClose} title={collapsed ? 'Settings' : undefined}>
            <Icon name="settings" /><span className="sidebarLinkText">Settings</span>
          </NavLink>}
          <button type="button" className="sidebarLogout" onClick={logout}><Icon name="logout" /><span className="sidebarLinkText">Log out</span></button>
        </div>
      </aside>
      <div className="notificationBellArea" ref={popoverRef}>
        <button type="button" className="globalNotificationBell" aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`} aria-expanded={popoverOpen} onClick={() => setPopoverOpen(value => !value)}><svg viewBox="0 0 24 24" aria-hidden="true">{iconPaths.notification}</svg><Badge count={unreadCount} className="globalNotificationBadge" /></button>
        {popoverOpen && (
          <div className="notificationPopover" role="dialog" aria-label="Recent notifications">
            <div className="notificationPopoverHeader"><strong>Notifications</strong><button type="button" onClick={markAllRead} disabled={!unreadCount}>Mark all read</button></div>
            <div className="notificationPopoverList">
              {recentStatus === 'loading' && <div className="notificationPopoverState" role="status">Loading…</div>}
              {recentStatus === 'error' && <div className="notificationPopoverState notificationError" role="alert">{recentError}</div>}
              {recentStatus === 'ready' && !recent.length && <div className="notificationPopoverState">No notifications yet.</div>}
              {recentStatus === 'ready' && recent.map(item => <NotificationCard key={item.notification_id} notification={item} onOpen={id => { setPopoverOpen(false); navigate(`/notifications/${id}/open`); }} />)}
            </div>
            <button type="button" className="notificationPopoverFooter" onClick={() => { setPopoverOpen(false); navigate('/notifications'); }}>View all notifications</button>
          </div>
        )}
      </div>
    </>
  );
};
